/**
 * La cuenta de Stripe del cliente: conectarla, consultarla y desconectarla.
 *
 * Vivía dentro de "Ventas y cobros", donde competía con las ofertas y hacía
 * parecer que había que conectar antes de poder montar nada. Es un ajuste del
 * proyecto —se toca una vez y no se vuelve—, así que su sitio es Configuración,
 * y la pantalla de ventas sólo enlaza aquí cuando falta algo.
 *
 * Lo que se conecta es la cuenta del CLIENTE, no la nuestra: el dinero de sus
 * ventas entra directo en ella y nosotros no lo tocamos en ningún momento. Por
 * eso la pantalla no promete "cobros a través de Veltica" en ninguna parte.
 *
 * El alta ocurre fuera, en el formulario alojado por Stripe —datos fiscales,
 * cuenta bancaria, identidad—, que es exactamente lo que no queremos pedir ni
 * guardar aquí. Al volver, esta pantalla relee el estado.
 */
import { useCallback, useEffect, useState } from 'react'
import type { BillingPlan } from '../../../lib/api'
import { PlatformFeeCard } from './PlatformFeeCard'
import { PLATFORM_FEE_PERCENT } from '../../../lib/planLimits'
import { ProjectSettingsLayout } from './ProjectSettingsLayout'
import { StripeOnboardingDialog } from './StripeOnboardingDialog'
import { ConfirmDialog } from '../ConfirmDialog'
import { useI18n } from '../../../lib/i18n'
import { regionName, useRegion } from '../../../lib/region'
import { useToast } from '../../Toast'
import {
  disconnectStripe,
  getStripeConnection,
  startStripeOnboarding,
  type ConnectStatus,
  type StripeConnection,
} from '../../../lib/api'
import { errorMessage } from './offerShared'
import { StripeMark } from './StripeMark'
import { isEmbeddedStripeConfigured } from './StripeEmbed'
import { StripeAttentionBanners } from './StripeAttentionBanners'
import { StripeAccountManagement } from './StripeAccountManagement'
import { StripeTaxSettings } from './StripeTaxSettings'
import {
  IconAlertTriangle,
  IconArrowUpRight,
  IconCheck,
  IconExternalLink,
  IconLoader2,
  IconPlugConnected,
  IconShieldCheck,
} from '@tabler/icons-react'

/** El texto de cada estado, en un solo sitio para que no se contradiga. */
const STATUS_COPY: Record<ConnectStatus, { label: string; tone: 'idle' | 'warn' | 'ok'; detail: string }> = {
  none: { label: 'stripe.status.none', tone: 'idle', detail: 'stripe.detail.none' },
  pending: { label: 'stripe.status.pending', tone: 'warn', detail: 'stripe.detail.pending' },
  restricted: { label: 'stripe.status.restricted', tone: 'warn', detail: 'stripe.detail.restricted' },
  ready: { label: 'stripe.status.ready', tone: 'ok', detail: 'stripe.detail.ready' },
}

export function StripeSettings({ projectId, token, canConnect = true, plan, onBack, onOpenOffers, onOpenPlans, onOpenRegion }: {
  projectId: string
  token: string
  /** El plan del dueño del sitio: de él sale la comisión que se enseña. */
  plan?: BillingPlan | undefined
  /**
   * Si el plan permite darse de alta. Ver `assertCanConnectStripe` en el
   * worker, que es donde está la reja: aquí sólo se dice antes de gastar el
   * gesto, y se ofrece el camino que sí lleva a alguna parte.
   *
   * Sin cuenta ninguna. Quien ya la tiene la conserva aunque baje de plan
   * —igual que sus ofertas publicadas—, así que el alta a medias se retoma sin
   * pasar por aquí.
   */
  canConnect?: boolean
  /** A Región, para corregir el país antes de crear la cuenta. */
  onOpenRegion?: (() => void) | undefined
  onBack?: (() => void) | undefined
  /** A donde se va después de conectar: lo que se vende. */
  onOpenOffers: () => void
  onOpenPlans?: (() => void) | undefined
}) {
  const { showToast } = useToast()
  const [connection, setConnection] = useState<StripeConnection | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  /** Qué botón está esperando a Stripe: sólo uno puede estarlo a la vez. */
  const [busy, setBusy] = useState<'connect' | null>(null)
  /* True mientras el alta está abierta en otra pestaña. Se apaga sola en cuanto
     la cuenta queda lista, y a mano con «Ya terminé». */
  const [awaitingOnboarding, setAwaitingOnboarding] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      /* Aquí sí se pregunta a Stripe cada vez: es la pantalla donde alguien
         está TERMINANDO el alta y vuelve de otra pestaña esperando la palomita.
         Con el estado cacheado, esa vuelta parecería no haber servido de nada. */
      const next = await getStripeConnection(projectId, token, { fresh: true })
      setConnection(next)
      setFailed(false)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [projectId, token])

  useEffect(() => { void load() }, [load])

  /**
   * Al volver del alta de Stripe se relee el estado.
   *
   * Stripe devuelve al cliente a esta misma dirección, así que la pestaña se
   * recarga sola y `load` ya corre. Este oyente cubre el otro camino: quien deja
   * el alta abierta en otra pestaña, la termina y vuelve a ésta sin recargar.
   */
  useEffect(() => {
    const onFocus = () => { void load() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [load])

  /**
   * Mientras el alta está abierta, se pregunta por la cuenta cada pocos
   * segundos.
   *
   * El `focus` de arriba ya releía al volver a esta pestaña, pero eso obliga a
   * volver: quien termina en Stripe se queda mirando su pestaña sin que aquí
   * pase nada. Sondear es lo que permite que la ventana se ponga en verde sola,
   * incluso sin tocar nada.
   *
   * Cada tres segundos y sólo mientras la ventana está abierta: el alta dura
   * minutos, no horas, y en cuanto la cuenta responde algo distinto de
   * «pendiente» se deja de preguntar.
   */
  useEffect(() => {
    if (!awaitingOnboarding) return
    if (connection?.status === 'ready' || connection?.status === 'restricted') return
    const timer = window.setInterval(() => { void load() }, 3000)
    return () => window.clearInterval(timer)
  }, [awaitingOnboarding, connection?.status, load])

  const status = connection?.status ?? 'none'
  const account = connection?.account ?? null
  const { t, language } = useI18n()
  const region = useRegion()
  const copy = STATUS_COPY[status]
  /* Sin plan la frase de arriba prometería un botón que no está. Sólo cuando no
     hay cuenta: la que ya existe se retoma con cualquier plan. */
  const planLocked = !account && !canConnect

  /**
   * Abre el alta de Stripe en una pestaña nueva, sin salir de véltica.
   *
   * La pestaña se abre AHORA, en el mismo gesto del clic, y se le pone la
   * dirección después. Es la única forma: la URL la da el servidor y llamar a
   * `window.open` tras el `await` cae fuera del gesto del usuario, que es
   * justamente cuando el navegador lo bloquea como popup. El mismo problema que
   * ya documenta el enlace al panel de Stripe unas líneas más abajo.
   *
   * Si el navegador la bloquea igualmente —hay quien lo tiene desactivado del
   * todo— se cae a la misma pestaña, que es lo que hacía antes: peor, pero
   * mejor que un botón que no hace nada.
   */
  const openOnboarding = async () => {
    const tab = window.open('about:blank', '_blank')
    setBusy('connect')
    try {
      const { url } = await startStripeOnboarding(projectId, token)
      if (tab) {
        tab.location.replace(url)
        setAwaitingOnboarding(true)
        setBusy(null)
      } else {
        window.location.href = url
      }
    } catch (error) {
      tab?.close()
      showToast(errorMessage(error), 'error')
      setBusy(null)
    }
  }

  const confirmDisconnect = async () => {
    try {
      await disconnectStripe(projectId, token)
      setDisconnectOpen(false)
      await load()
      showToast(t('stripe.disconnected'))
    } catch (error) {
      showToast(errorMessage(error), 'error')
    }
  }

  /* Los tres pasos, cada uno de un dato de la cuenta y no del texto: así la
     barra no puede decir «falta conectar» a quien ya conectó. */
  const steps = [
    { key: 'connect', label: 'stripe.progress.connect', done: account !== null },
    { key: 'details', label: 'stripe.progress.details', done: account?.detailsSubmitted === true },
    { key: 'charges', label: 'stripe.progress.charges', done: account?.chargesEnabled === true },
  ]
  const currentStep = steps.findIndex((step) => !step.done)
  /* En el gratis la comisión tiene su propia tarjeta, con el ahorro de Pro. */
  const showFeeFact = plan !== undefined && plan !== 'free'

  return (
    <ProjectSettingsLayout
      title={t('stripe.title')}
      description={t('stripe.settings.description')}
      onBack={onBack}
    >
      {/* El alta, mientras ocurre en la otra pestaña.
          Una ventana y no una banda: mientras dura no hay nada más que hacer en
          esta pantalla, y una banda invita a seguir tocando cosas que van a
          cambiar debajo. Se cierra sola al quedar lista. */}
      {awaitingOnboarding ? (
        <StripeOnboardingDialog
          status={status}
          onClose={() => { setAwaitingOnboarding(false); void load() }}
        />
      ) : null}

      {/* Lo que Stripe pide y los impuestos a medias, arriba y en el banner de
          los avisos que esperan respuesta. Ver `StripeAttentionBanners`. */}
      <StripeAttentionBanners projectId={projectId} token={token} />

      {/* ── ¿Puedo cobrar? ───────────────────────────────────────────────────
          La pregunta con la que se llega a esta pantalla, contestada arriba y
          en grande. Estaba repartida en cinco sitios —una insignia en la
          cabecera de una tarjeta, una frase debajo, el aviso de Stripe en otro
          bloque y una lista de «Primeros pasos» al final que era fija y seguía
          diciendo «conecta Stripe» a quien ya lo había hecho—, y había que
          juntarlos para saber qué pasaba. Ahora es un titular, tres pasos que
          salen del estado real de la cuenta y un solo botón: lo siguiente. */}
      <section className={`dash-stripe-hero is-${loading || failed ? 'idle' : copy.tone}`} aria-live="polite">
        {loading ? (
          <p className="dash-stripe-hero-body">{t('stripe.checking')}</p>
        ) : failed ? (
          <div className="dash-inline-msg">
            <span>{t('stripe.checkFailed')}</span>
            <div className="dash-inline-msg-actions">
              <button type="button" className="dash-secondary-btn" onClick={() => { setLoading(true); void load() }}>
                {t('stripe.retry')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Una fila de identidad —logo, nombre e insignia juntos— y debajo
                el titular, al mismo borde izquierdo que los pasos y el botón.
                Con el logo en su propia columna, el texto empezaba desplazado y
                la insignia se iba sola a la otra punta. */}
            <div className="dash-stripe-hero-head">
              <span className="dash-stripe-hero-mark" aria-hidden="true">
                <StripeMark size={18} color="#635BFF" />
              </span>
              <strong>{t('stripe.title')}</strong>
              <span className={`dash-connect-status is-${copy.tone}`}>
                {copy.tone === 'ok' ? <IconShieldCheck size={14} /> : copy.tone === 'warn' ? <IconAlertTriangle size={14} /> : null}
                {t(copy.label)}
              </span>
            </div>
            <div className="dash-stripe-hero-copy">
              <h2>{t(`stripe.hero.${status}.title`)}</h2>
              <p className="dash-stripe-hero-body">{t(planLocked ? 'stripe.detail.planLocked' : `stripe.hero.${status}.body`)}</p>
            </div>

            {/* Los tres pasos del alta, con el estado de verdad: la cuenta
                existe, Stripe tiene los datos y Stripe activó los cobros. El
                primero que falta es «el tuyo». Con la cuenta lista se van: tres
                palomitas no dicen nada que el titular no diga ya. */}
            {status !== 'ready' ? (
            <ol className="dash-stripe-progress">
              {steps.map((step, index) => (
                <li key={step.key} className={step.done ? 'is-done' : index === currentStep ? 'is-current' : ''}>
                  <span className="dash-stripe-progress-dot" aria-hidden="true">
                    {step.done ? <IconCheck size={13} /> : index + 1}
                  </span>
                  <span className="dash-stripe-progress-label">
                    {t(step.label)}
                    <span className="dash-sr-only">
                      {' · '}{t(step.done ? 'stripe.progress.done' : index === currentStep ? 'stripe.progress.current' : 'stripe.progress.next')}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
            ) : null}

            {/* Los datos de la cuenta lista y, en los planes de pago, la
                comisión de véltica: ahí no hay plan mejor que ofrecer, así que
                es un dato más y no una tarjeta. En el gratis la cuenta con
                dinero `PlatformFeeCard`, más abajo. */}
            {(account && status === 'ready') || showFeeFact ? (
              <dl className="dash-connect-facts dash-stripe-hero-facts">
                {account && status === 'ready' ? (
                  <>
                    <div>
                      <dt>{t('stripe.fact.country')}</dt>
                      <dd>{account.country || '—'}</dd>
                    </div>
                    <div>
                      <dt>{t('stripe.fact.currency')}</dt>
                      <dd>{account.defaultCurrency ? account.defaultCurrency.toUpperCase() : '—'}</dd>
                    </div>
                    <div>
                      <dt>{t('stripe.fact.payouts')}</dt>
                      <dd>{t(account.payoutsEnabled ? 'stripe.payouts.on' : 'stripe.payouts.off')}</dd>
                    </div>
                  </>
                ) : null}
                {showFeeFact && plan ? (
                  <div>
                    <dt>{t('stripe.fact.fee')}</dt>
                    <dd>{t('platformFee.perSale', { fee: PLATFORM_FEE_PERCENT[plan] })}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}

            {/* Antes de crear la cuenta, en qué país va a nacer: Stripe no deja
                cambiarlo, así que es ahora o nunca. Sale de la Región. */}
            {status === 'none' && !planLocked && !region.loading ? (
              <p className="dash-stripe-country-hint">
                {t('stripe.countryHint', { country: regionName(region.countryCode, language) })}
                {onOpenRegion ? (
                  <>
                    {' '}
                    <button type="button" className="dash-link-btn" onClick={onOpenRegion}>{t('stripe.countryChange')}</button>
                  </>
                ) : null}
              </p>
            ) : null}
            <div className="dash-stripe-hero-actions">
              {status === 'ready' || status === 'restricted' ? (
                /* Un enlace y no un botón: el panel de Stripe está en una URL
                   fija, así que no hay nada que pedir al servidor antes de
                   abrirlo. Cuando esto llamaba a la API primero, el
                   `window.open` caía después del `await` —fuera ya del gesto
                   del usuario— y el navegador lo bloqueaba como popup. */
                <a
                  className={status === 'ready' ? 'dash-secondary-btn' : 'dash-primary-btn'}
                  href="https://dashboard.stripe.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconExternalLink size={14} />
                  {t('stripe.openDashboard')}
                </a>
              ) : planLocked ? (
                /* Sin plan no se abre el alta: Stripe crearía la cuenta y el
                   servidor la rechazaría un paso después. */
                <button type="button" className="dash-primary-btn" onClick={onOpenPlans}>
                  {t('stripe.seePlans')}
                  <IconArrowUpRight size={14} />
                </button>
              ) : (
                <button type="button" className="dash-primary-btn" disabled={busy === 'connect'} onClick={() => { void openOnboarding() }}>
                  {busy === 'connect' ? <IconLoader2 className="dash-spin" size={14} /> : <IconPlugConnected size={14} />}
                  {t(status === 'none' ? 'stripe.connect' : 'stripe.continueSetup')}
                  <IconArrowUpRight size={14} />
                </button>
              )}
              {/* Lo que se puede hacer mientras: el catálogo no espera a
                  Stripe, y con la cuenta lista es a donde se va después. */}
              <button type="button" className="dash-ghost-btn" onClick={onOpenOffers}>
                {t(status === 'ready' ? 'stripe.hero.goOffers' : 'stripe.hero.meanwhile')}
              </button>
            </div>
          </>
        )}
      </section>


      {/* ── Cómo se cobra cada venta ─────────────────────────────────────────
          Lo que pasa con el dinero de cada venta: cuánto se queda véltica y
          los impuestos. La comisión sale también sin cuenta: es lo que hay que
          saber ANTES de conectar, no después del primer depósito. Los impuestos
          esperan a la cuenta, que es donde se configuran. */}
      {plan === 'free' ? (
        <section className="dash-config-group">
          <h2 className="dash-config-group-title">{t('stripe.group.sale')}</h2>
          <PlatformFeeCard projectId={projectId} token={token} plan={plan} onOpenPlans={onOpenPlans} />
        </section>
      ) : null}

      {/* ── Tu cuenta ────────────────────────────────────────────────────────
          Los datos del negocio y, al fondo y sin color, la salida. Desconectar
          iba al lado de «Abrir panel de Stripe», con el mismo peso: el botón
          que apaga los cobros no puede estar a un error de clic del que sólo
          los consulta. */}
      {account ? (
        <section className="dash-config-group">
          <h2 className="dash-config-group-title">{t('stripe.group.account')}</h2>
          <StripeAccountManagement>
            {/* Los impuestos, como una fila más: su estado en una línea, el
                interruptor y la configuración de Stripe en una ventana. */}
            {isEmbeddedStripeConfigured() ? <StripeTaxSettings projectId={projectId} token={token} /> : null}
            {/* La salida, como última fila de la misma lista y sin color de
                fondo: se ve, pero no al lado del botón que sólo consulta. */}
            <div className="dash-stripe-account-row is-danger">
              <span>
                <strong>{t(status === 'ready' ? 'stripe.disconnect.title' : 'stripe.resetSetup')}</strong>
                <small>{t(status === 'ready' ? 'stripe.disconnect.short' : 'stripe.danger.resetShort')}</small>
              </span>
              <button type="button" className="dash-ghost-btn" onClick={() => setDisconnectOpen(true)}>
                {t(status === 'ready' ? 'stripe.disconnect' : 'stripe.resetSetup')}
              </button>
            </div>
          </StripeAccountManagement>
        </section>
      ) : null}

      {disconnectOpen ? (
        <ConfirmDialog
          title={t('stripe.disconnect.title')}
          message={t('stripe.disconnect.body')}
          confirmLabel={t('stripe.disconnect')}
          tone="danger"
          onCancel={() => setDisconnectOpen(false)}
          onConfirm={confirmDisconnect}
        />
      ) : null}
    </ProjectSettingsLayout>
  )
}
